import { getMinQ, getOrderStep } from '../../../shared/utils/pricing.utils';

export interface SavedListProduct {
    id: string;
    name: string;
    slug: string;
    gtin?: string;
    targetPrice?: number | null; // Minor units, EUR, excluding VAT.
    quantity?: number | null;
}
export interface SavedProductList { id?: string; name: string; products: SavedListProduct[]; updatedAt?: string; revision?: number; }
export interface ListOffer {
    id: string;
    price: number;
    priceWithTax: number;
    moq?: number;
    stockLevels: Array<{ stockOnHand: number; stockAllocated: number }>;
    pricing: { id: string; currency?: string; validFrom?: string; validTo?: string; fullPackageOnly?: boolean };
    pricingProduct?: { minQ?: number; maxQ?: number };
}
export interface ListProduct {
    slug?: string;
    id: string;
    name: string;
    enabled: boolean;
    customFields?: { gtin?: string; brand?: string; quantityPerBox?: number };
    featuredAsset?: { preview: string };
    variants: ListOffer[];
}
export interface ComparisonRow {
    saved: SavedListProduct;
    product: ListProduct | null;
    offer: ListOffer | null;
    available: number;
    lowestQuantity: number;
    quantity: number;
    difference: number | null;
    status: 'better' | 'close' | 'higher' | 'none' | 'error';
    retail?: { min: number | null; currency: string | null };
}
export function offerStock(offer: ListOffer): number {
    return offer.stockLevels.reduce((sum, stock) => sum + Math.max(0, stock.stockOnHand - stock.stockAllocated), 0);
}
export function orderLimit(offer: ListOffer): number {
    const max = offer.pricingProduct?.maxQ;
    return Math.min(offerStock(offer), max != null && max > 0 ? max : Infinity);
}
export function orderQuantity(offer: ListOffer, product: ListProduct, wanted: number, inCart = 0): number {
    const step = getOrderStep(offer, product);
    const minimum = Math.max(1, getMinQ(offer));
    const remaining = Math.floor(Math.max(0, orderLimit(offer) - inCart) / step) * step;
    const requested = Math.ceil(Math.max(minimum - inCart, wanted, step) / step) * step;
    return remaining >= Math.ceil(Math.max(1, minimum - inCart) / step) * step ? Math.min(requested, remaining) : 0;
}
export function compareProduct(saved: SavedListProduct, product: ListProduct | null, failed = false, now = Date.now()): ComparisonRow {
    const offers = (product?.enabled ? product.variants : []).filter(offer => {
        const from = offer.pricing?.validFrom ? Date.parse(offer.pricing.validFrom) : -Infinity;
        const to = offer.pricing?.validTo ? Date.parse(offer.pricing.validTo) : Infinity;
        return offer.pricing?.currency === 'EUR' && offer.price > 0 && from <= now && to >= now &&
            orderQuantity(offer, product!, 1) > 0;
    }).sort((a, b) => a.price - b.price);
    const offer = offers[0] ?? null;
    const target = saved.targetPrice;
    const difference = offer && target != null && target > 0 ? (offer.price - target) / target * 100 : null;
    return {
        saved, product, offer, difference,
        available: offers.reduce((sum, item) => sum + offerStock(item), 0),
        lowestQuantity: offer ? offerStock(offer) : 0,
        quantity: offer ? orderQuantity(offer, product!, saved.quantity ?? 1) : 0,
        status: failed ? 'error' : !offer ? 'none' : difference == null ? 'higher' : difference < -5 ? 'better' : difference <= 5 ? 'close' : 'higher',
    };
}
